/**
 * Internationalization (i18n) Helper
 * @module chassis/helpers/i18n
 *
 * Provides utility functions for working with internationalization across the application.
 * Supports translation retrieval, language validation, and batch operations.
 *
 * @example
 * import { t, getTranslations } from '@/chassis/helpers/i18n';
 * import { i18n } from '@/domains/shared/i18n';
 *
 * // Get a single translation
 * const label = t(i18n, 'common.actions.save', 'pt'); // "Salvar"
 *
 * // Get all translations for a language
 * const allPt = getTranslations(i18n, 'pt');
 */

// ==========================================
// Types
// ==========================================

export type Language = 'pt' | 'en' | 'es';

export type I18nDictionary = Record<string, Record<Language, string>>;

export type I18nKey<T extends I18nDictionary> = keyof T;

// ==========================================
// Core Functions
// ==========================================

/**
 * Get translation for a specific key and language
 * @param dictionary - The i18n dictionary object
 * @param key - The translation key
 * @param lang - The language code (pt, en, es)
 * @param fallbackLang - Fallback language if translation not found (default: 'pt')
 * @returns Translated string or key if not found
 *
 * @example
 * const text = t(i18n, 'common.actions.save', 'en'); // "Save"
 */
export function t<T extends I18nDictionary>(dictionary: T, key: keyof T | string, lang: Language = 'pt', fallbackLang: Language = 'pt'): string {
    const entry = dictionary[key as keyof T];

    if (!entry) {
        console.warn(`Translation key "${String(key)}" not found`);
        return String(key);
    }

    // Try requested language
    if (entry[lang]) {
        return entry[lang];
    }

    // Try fallback language
    if (entry[fallbackLang]) {
        console.warn(`Translation for "${String(key)}" not found in "${lang}", using fallback "${fallbackLang}"`);
        return entry[fallbackLang];
    }

    // Return first available translation
    const firstAvailable = Object.values(entry)[0];
    if (firstAvailable) {
        console.warn(`Translation for "${String(key)}" not found in "${lang}" or "${fallbackLang}", using first available`);
        return firstAvailable;
    }

    return String(key);
}

/**
 * Get all translations for a specific language from a dictionary
 * @param dictionary - The i18n dictionary object
 * @param lang - The language code (pt, en, es)
 * @returns Object with all translations for the language
 *
 * @example
 * const translations = getTranslations(i18n, 'pt');
 * console.log(translations['common.actions.save']); // "Salvar"
 */
export function getTranslations<T extends I18nDictionary>(dictionary: T, lang: Language = 'pt'): Record<string, string> {
    const translations: Record<string, string> = {};

    Object.keys(dictionary).forEach((key) => {
        translations[key] = t(dictionary, key, lang);
    });

    return translations;
}

/**
 * Check if a language is supported
 * @param lang - The language code to check
 * @returns True if language is supported
 *
 * @example
 * if (isLanguageSupported('pt')) {
 *   // Language is supported
 * }
 */
export function isLanguageSupported(lang: string): lang is Language {
    return ['pt', 'en', 'es'].includes(lang);
}

/**
 * Get the list of supported languages
 * @returns Array of supported language codes
 */
export function getSupportedLanguages(): Language[] {
    return ['pt', 'en', 'es'];
}

/**
 * Get language display name
 * @param lang - The language code
 * @param displayLang - Language for the display name (default: same as lang)
 * @returns Display name for the language
 *
 * @example
 * getLanguageName('pt', 'en'); // "Portuguese"
 * getLanguageName('en', 'pt'); // "Inglês"
 */
export function getLanguageName(lang: Language, displayLang: Language = lang): string {
    const names: Record<Language, Record<Language, string>> = {
        pt: {
            pt: 'Português',
            en: 'Portuguese',
            es: 'Portugués',
        },
        en: {
            pt: 'Inglês',
            en: 'English',
            es: 'Inglés',
        },
        es: {
            pt: 'Espanhol',
            en: 'Spanish',
            es: 'Español',
        },
    };

    return names[lang]?.[displayLang] || lang;
}

/**
 * Get language flag emoji
 * @param lang - The language code
 * @returns Flag emoji for the language
 */
export function getLanguageFlag(lang: Language): string {
    const flags: Record<Language, string> = {
        pt: '🇧🇷',
        en: '🇺🇸',
        es: '🇪🇸',
    };

    return flags[lang] || '🌐';
}

/**
 * Merge multiple i18n dictionaries
 * @param dictionaries - Array of i18n dictionaries to merge
 * @returns Merged dictionary
 *
 * @example
 * const merged = mergeI18nDictionaries(commonI18n, moduleI18n);
 */
export function mergeI18nDictionaries<T extends I18nDictionary>(...dictionaries: T[]): I18nDictionary {
    return Object.assign({}, ...dictionaries);
}

/**
 * Create a translation function bound to a specific dictionary and language
 * @param dictionary - The i18n dictionary
 * @param lang - The language to use
 * @returns Translation function
 *
 * @example
 * const tPt = createTranslator(i18n, 'pt');
 * tPt('common.actions.save'); // "Salvar"
 */
export function createTranslator<T extends I18nDictionary>(dictionary: T, lang: Language = 'pt'): (key: keyof T | string) => string {
    return (key: keyof T | string) => t(dictionary, key, lang);
}

/**
 * Get translation with interpolation
 * @param dictionary - The i18n dictionary
 * @param key - The translation key
 * @param lang - The language code
 * @param params - Parameters to interpolate
 * @returns Translated and interpolated string
 *
 * @example
 * // Dictionary has: 'greeting': { pt: 'Olá, {name}!', ... }
 * const text = tInterpolate(i18n, 'greeting', 'pt', { name: 'João' });
 * // "Olá, João!"
 */
export function tInterpolate<T extends I18nDictionary>(dictionary: T, key: keyof T | string, lang: Language, params: Record<string, string | number>): string {
    let text = t(dictionary, key, lang);

    Object.keys(params).forEach((paramKey) => {
        const placeholder = `{${paramKey}}`;
        text = text.replace(new RegExp(placeholder, 'g'), String(params[paramKey]));
    });

    return text;
}

/**
 * Check if a translation key exists in the dictionary
 * @param dictionary - The i18n dictionary
 * @param key - The key to check
 * @returns True if key exists
 */
export function hasTranslation<T extends I18nDictionary>(dictionary: T, key: string): boolean {
    return key in dictionary;
}

/**
 * Get all keys from an i18n dictionary
 * @param dictionary - The i18n dictionary
 * @returns Array of all keys
 */
export function getTranslationKeys<T extends I18nDictionary>(dictionary: T): Array<keyof T> {
    return Object.keys(dictionary) as Array<keyof T>;
}

/**
 * Filter translations by prefix
 * @param dictionary - The i18n dictionary
 * @param prefix - The prefix to filter by
 * @param lang - The language code
 * @returns Object with filtered translations
 *
 * @example
 * const actions = getTranslationsByPrefix(i18n, 'common.actions', 'pt');
 * // Returns all translations that start with 'common.actions'
 */
export function getTranslationsByPrefix<T extends I18nDictionary>(dictionary: T, prefix: string, lang: Language = 'pt'): Record<string, string> {
    const filtered: Record<string, string> = {};

    Object.keys(dictionary).forEach((key) => {
        if (key.startsWith(prefix)) {
            filtered[key] = t(dictionary, key, lang);
        }
    });

    return filtered;
}

// ==========================================
// Browser/Storage Integration
// ==========================================

/**
 * Get user's preferred language from browser
 * @returns Detected language or default
 */
export function getBrowserLanguage(): Language {
    const browserLang = navigator.language.toLowerCase();

    if (browserLang.startsWith('pt')) return 'pt';
    if (browserLang.startsWith('en')) return 'en';
    if (browserLang.startsWith('es')) return 'es';

    return 'pt'; // Default
}

/**
 * Save language preference to localStorage
 * @param lang - The language to save
 */
export function saveLanguagePreference(lang: Language): void {
    try {
        localStorage.setItem('app_language', lang);
    } catch (error) {
        console.warn('Failed to save language preference:', error);
    }
}

/**
 * Load language preference from localStorage
 * @returns Saved language or null
 */
export function loadLanguagePreference(): Language | null {
    try {
        const saved = localStorage.getItem('app_language');
        if (saved && isLanguageSupported(saved)) {
            return saved;
        }
    } catch (error) {
        console.warn('Failed to load language preference:', error);
    }

    return null;
}

/**
 * Get effective language (preference > browser > default)
 * @returns The language to use
 */
export function getEffectiveLanguage(): Language {
    return loadLanguagePreference() || getBrowserLanguage() || 'pt';
}

// ==========================================
// Export all functions
// ==========================================

export default {
    t,
    getTranslations,
    isLanguageSupported,
    getSupportedLanguages,
    getLanguageName,
    getLanguageFlag,
    mergeI18nDictionaries,
    createTranslator,
    tInterpolate,
    hasTranslation,
    getTranslationKeys,
    getTranslationsByPrefix,
    getBrowserLanguage,
    saveLanguagePreference,
    loadLanguagePreference,
    getEffectiveLanguage,
};
